import { ArrowLeft, Home } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import Footer from '../components/Footer';
import { useSafeBack } from '../hooks/useSafeBack';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const { goBack } = useSafeBack('/');

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="flex-1">
        <div className="mx-auto max-w-3xl px-4 py-8">
          {/* Back Button + Home */}
          <div className="flex items-center gap-2 mb-6">
            <Button
              variant="ghost"
              onClick={goBack}
              className="h-10 px-0 text-sm font-medium text-zinc-500 hover:text-zinc-900 hover:bg-transparent"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard')}
              className="h-8 w-8"
              data-testid="nav-home-btn"
              aria-label="Home"
            >
              <Home className="w-4 h-4 text-zinc-400" />
            </Button>
          </div>

          {/* Header */}
          <h1 className="text-3xl font-bold text-zinc-900 mb-2">Privacy Policy</h1>
          <p className="text-sm text-zinc-500 mb-1">Effective Date: May 19, 2026</p>
          <p className="text-sm text-zinc-500 mb-8">Last Updated: May 19, 2026</p>

          {/* Content */}
          <div className="prose prose-zinc max-w-none">
            <p className="text-zinc-700 leading-relaxed mb-8">
              Open Runs USA ("Open Runs", "we", "our", or "us") operates a basketball pickup game coordination platform that allows users to create, discover, and participate in basketball games.
            </p>

            {/* Information We Collect */}
            <h2 className="text-xl font-semibold text-zinc-900 mt-8 mb-4">Information We Collect</h2>
            <p className="text-zinc-700 mb-4">We collect the following information when you use Open Runs USA:</p>
            <ul className="list-disc pl-6 text-zinc-700 space-y-2 mb-6">
              <li>
                <strong>Account information:</strong> name or username, email address, phone number, date of birth (used for age verification only — we display only your age, never your date of birth), and an optional profile photo.
              </li>
              <li>
                <strong>Profile details:</strong> skill level, grade level (if you are under 18), city or area, and any clubs you create or join.
              </li>
              <li>
                <strong>Game activity:</strong> the runs you create, join, leave, or comment on; players you follow, block, or invite; your roster confirmations and waitlist actions.
              </li>
              <li>
                <strong>Notification preferences:</strong> your SMS opt-in status, email preferences, and any notification you have marked as read.
              </li>
              <li>
                <strong>Device and technical information:</strong> browser type, operating system, IP address (for security and abuse prevention), and aggregated session metrics.
              </li>
              <li>
                <strong>Communications:</strong> messages you send to support via email or in-app chat.
              </li>
            </ul>

            {/* How We Use Your Information */}
            <h2 className="text-xl font-semibold text-zinc-900 mt-8 mb-4">How We Use Your Information</h2>
            <p className="text-zinc-700 mb-4">We use information to:</p>
            <ul className="list-disc pl-6 text-zinc-700 space-y-2 mb-6">
              <li>Create and manage accounts</li>
              <li>Allow users to create and join games</li>
              <li>Send notifications and reminders</li>
              <li>Improve safety and performance of the platform</li>
            </ul>

            {/* SMS Messaging */}
            <h2 className="text-xl font-semibold text-zinc-900 mt-8 mb-4">SMS Messaging</h2>
            <p className="text-zinc-700 mb-4">
              By providing your mobile phone number and opting in, you consent to receive SMS messages from Open Runs USA at the phone number provided related to game confirmations, game invitations, game reminders, and account notifications.
            </p>
            <p className="text-zinc-700 mb-4">
              Message frequency may vary. Message and data rates may apply.
            </p>
            <p className="text-zinc-700 mb-4">
              You can opt out at any time by replying STOP. For help, reply HELP or contact{' '}
              <a href="mailto:openrunshelp@gmail.com" className="text-orange-600 hover:text-orange-700 underline">
                openrunshelp@gmail.com
              </a>.
            </p>
            <p className="text-zinc-700 mb-6">
              Open Runs USA does not sell, rent, or share your mobile phone number or SMS consent information with third parties for marketing purposes.
            </p>

            {/* Third-Party Service Providers */}
            <h2 className="text-xl font-semibold text-zinc-900 mt-8 mb-4">Third-Party Service Providers</h2>
            <p className="text-zinc-700 mb-4">
              We use a small set of trusted third-party providers to operate Open Runs USA. These providers process information only on our behalf and only to the extent necessary to deliver the service:
            </p>
            <ul className="list-disc pl-6 text-zinc-700 space-y-2 mb-4">
              <li>
                <strong>MongoDB Atlas</strong> (United States) — primary database hosting. Stores all account, run, club, and notification data.
              </li>
              <li>
                <strong>Twilio</strong> (United States) — SMS delivery and phone-number verification. Receives your phone number and the contents of SMS messages we send to you.
              </li>
              <li>
                <strong>Resend</strong> (United States) — transactional email delivery. Receives your email address and the contents of emails we send to you.
              </li>
              <li>
                <strong>Google Maps Places API</strong> (Google LLC, United States) — location autocomplete for clubs and runs. Receives the location text you type into our location search.
              </li>
              <li>
                <strong>Crisp Chat</strong> (Crisp IM, France) — in-app and web-based customer support widget. Receives messages you send to support, your username, and your session identifier.
              </li>
            </ul>
            <p className="text-zinc-700 mb-6">
              We do not sell or rent personal information to third parties for marketing purposes. We do not use third-party advertising networks or tracking pixels.
            </p>

            {/* Data Security */}
            <h2 className="text-xl font-semibold text-zinc-900 mt-8 mb-4">Data Security</h2>
            <p className="text-zinc-700 mb-4">
              We protect your information using industry-standard practices including:
            </p>
            <ul className="list-disc pl-6 text-zinc-700 space-y-2 mb-4">
              <li>
                <strong>Encryption in transit:</strong> All data exchanged between the app and our servers is encrypted using TLS 1.2 or higher.
              </li>
              <li>
                <strong>Encryption at rest:</strong> Stored data is encrypted by our hosting provider (MongoDB Atlas).
              </li>
              <li>
                <strong>Authentication:</strong> Passwords are stored as one-way bcrypt hashes; we never see your raw password. Phone-number login uses time-limited, single-use one-time codes (OTP).
              </li>
              <li>
                <strong>Access controls:</strong> Internal access to production data is restricted to the smallest set of administrators required to operate the service.
              </li>
            </ul>
            <p className="text-zinc-700 mb-6">
              No system is 100% secure. If we discover a breach affecting your information, we will notify you as soon as reasonably practical and in accordance with applicable law.
            </p>

            {/* Data Retention */}
            <h2 className="text-xl font-semibold text-zinc-900 mt-8 mb-4">Data Retention</h2>
            <p className="text-zinc-700 mb-4">
              We retain your personal information for as long as your account is active. When you delete your account, we permanently remove your profile, contact information, follows, comments, hosted run records, in-app messages, and account preferences within 30 days. Aggregated, non-identifying audit logs (e.g., counts of runs created, anonymous platform usage statistics) may be retained indefinitely for service-improvement purposes.
            </p>
            <p className="text-zinc-700 mb-6">
              Communication records required to demonstrate SMS opt-in consent (per CTIA / TCPA compliance) may be retained for up to 4 years after account deletion, in a form that does not associate the record with an active user.
            </p>

            {/* Your Rights and Account Deletion */}
            <h2 className="text-xl font-semibold text-zinc-900 mt-8 mb-4">Your Rights and Account Deletion</h2>
            <p className="text-zinc-700 mb-4">
              You can <strong>delete your account at any time</strong> directly inside the app:
              <br />
              <em>Profile → Account Settings → Delete Account.</em>
            </p>
            <p className="text-zinc-700 mb-4">
              Account deletion is permanent and immediate. It cannot be undone. Deletion removes your profile, follows, comments, notifications, and hosted runs. Roster entries on other users' games are removed, but the games themselves remain intact for the other participants.
            </p>
            <p className="text-zinc-700 mb-4">
              You may also request data deletion or correction by contacting us at{' '}
              <a href="mailto:openrunshelp@gmail.com" className="text-orange-600 hover:text-orange-700 underline">
                openrunshelp@gmail.com
              </a>. We respond within 30 days.
            </p>
            <p className="text-zinc-700 mb-6">
              California, Colorado, Connecticut, Utah, and Virginia residents have additional rights under their respective state privacy laws (CCPA/CPRA, CPA, CTDPA, UCPA, VCDPA), including the right to know what personal information we hold and the right to request deletion. To exercise these rights, contact us at the address above.
            </p>

            {/* Children's Privacy */}
            <h2 className="text-xl font-semibold text-zinc-900 mt-8 mb-4">Children's Privacy (Under 13)</h2>
            <p className="text-zinc-700 mb-4">
              Open Runs USA is not intended for users under the age of 13. We do not knowingly collect personal information from children under 13. Our signup flow requires a date of birth, and accounts attempting to register with an age under 13 are blocked.
            </p>
            <p className="text-zinc-700 mb-6">
              If you believe we have inadvertently collected information from a child under 13, please contact us at{' '}
              <a href="mailto:openrunshelp@gmail.com" className="text-orange-600 hover:text-orange-700 underline">
                openrunshelp@gmail.com
              </a>{' '}
              and we will delete the account and all associated information promptly.
            </p>

            <h3 className="text-lg font-semibold text-zinc-900 mt-6 mb-3">Users Aged 13–17 (Minors)</h3>
            <p className="text-zinc-700 mb-4">
              Users aged 13 to 17 may use Open Runs USA with parental awareness. We apply additional safety protections to minor accounts, including:
            </p>
            <ul className="list-disc pl-6 text-zinc-700 space-y-2 mb-4">
              <li>Minor users cannot see or join runs designated as adult-only (ages 18+ or any range that excludes their age).</li>
              <li>The rosters and comments on adult-only runs are hidden from minor users.</li>
              <li>Recommendation engines do not cross-suggest minors and adults to one another.</li>
            </ul>
            <p className="text-zinc-700 mb-6">
              A parent or legal guardian may request access to a minor's account information or request deletion of a minor's account by contacting{' '}
              <a href="mailto:openrunshelp@gmail.com" className="text-orange-600 hover:text-orange-700 underline">
                openrunshelp@gmail.com
              </a>.
            </p>

            {/* Contact */}
            <h2 className="text-xl font-semibold text-zinc-900 mt-8 mb-4">Contact</h2>
            <p className="text-zinc-700 mb-6">
              <a href="mailto:openrunshelp@gmail.com" className="text-orange-600 hover:text-orange-700 underline">
                openrunshelp@gmail.com
              </a>
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
